import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useLanguage } from '@/context/LanguageContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const COOKIE_KEY = 'cca_tour_seen_v1';
const LIBRARY_TOUR_KEY = 'cca_library_tour_seen_v1';
// Tour language is its own preference (the user may use the site in EN but
// want the tour narrated in ES, or vice-versa). LibraryTour reads the same
// key so the two tours stay narratively consistent.
const TOUR_LANG_KEY = 'cca_tour_lang';

/**
 * First-visit guided tour using driver.js.
 *
 * Flow:
 *   1. User dispatches `cca:start-tour` (via TakeTourBubble on '/',
 *      DetectiveDinoBot's "Take the tour again", or the About page link).
 *   2. We show a small bilingual language-picker dialog asking which
 *      language to narrate the tour in.
 *   3. The chosen language is stashed in localStorage so the follow-up
 *      LibraryTour keeps the same voice.
 *   4. driver.js walks the user through the header, the hamburger
 *      menu (item-by-item with the menu auto-opened), each Linktree
 *      pill, the social/follow row, and finally offers a deeper-dive
 *      Library tour.
 *
 * Why manual-only: an auto-firing tour triggered a React-19 unmount
 * race (driver.js mutates DOM out of React's knowledge). See the helmet
 * swap entry in CHANGELOG for the full story.
 */
const FirstVisitTour = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isSpanish: siteIsSpanish } = useLanguage();
  const drvRef = useRef(null);
  const [showLangPicker, setShowLangPicker] = useState(false);

  // ---------- helpers for the hamburger-menu walkthrough ----------

  // The Radix DropdownMenu portals menu items into the body only while
  // open. To highlight individual items, we need to make sure the menu
  // is open before driver.js looks for them.
  const openMenuIfClosed = useCallback(() => {
    if (!document.querySelector('[data-testid="menu-home"]')) {
      document.querySelector('[data-testid="header-menu-btn"]')?.click();
    }
  }, []);

  const closeMenuIfOpen = useCallback(() => {
    if (document.querySelector('[data-testid="menu-home"]')) {
      // Radix listens for the Escape key globally.
      document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
    }
  }, []);

  // ---------- main tour runner ----------

  const runTour = useCallback((langOverride) => {
    const useEs = langOverride === 'es';
    try { localStorage.setItem(TOUR_LANG_KEY, useEs ? 'es' : 'en'); } catch { /* ignore */ }
    try { localStorage.removeItem(COOKIE_KEY); } catch { /* ignore */ }

    // Helper for menu-step `onHighlightStarted` — clears any stale
    // `.driver-active-element` class left on a previous menu item
    // before driver.js applies the new highlight. Driver.js v1.4
    // sometimes fails to un-highlight when steps target portalled
    // elements (Radix DropdownMenu items here), so we belt-and-braces
    // it ourselves to prevent the spotlight stacking.
    const clearStaleHighlight = () => {
      document.querySelectorAll('.driver-active-element').forEach((el) => {
        el.classList.remove('driver-active-element');
      });
    };

    // ----- English steps -----
    const stepsEn = [
      // Header — logo → install → language → menu in their literal
      // left-to-right visual order so the tour reads naturally.
      {
        element: '[data-testid="header-logo"]',
        popover: { title: '🦖 The logo is your "Home" button', description: 'Tap the Detective Dino logo any time from any page to come right back to this homepage.', side: 'bottom', align: 'start' },
      },
      {
        element: '[data-testid="header-install-wrap"]',
        popover: { title: '⬇️ Install', description: 'Add ClearCommunicationApp to your home screen as a PWA, or grab the App Store / Play Store / Amazon Fire app.', side: 'bottom', align: 'end' },
      },
      {
        element: '[data-testid="header-language-toggle"]',
        popover: { title: '🌐 Language', description: 'Switch between English and Español at any time. The whole site translates.', side: 'bottom', align: 'end' },
      },

      // Menu button itself. Clicking "Next" dispatches the force-open
      // event so Header's DropdownMenu becomes a controlled-open state,
      // then advances after a tick so Radix has time to portal the items
      // into the DOM (without this, driver.js queries the items before
      // they exist and falls back to its hidden dummy element, which is
      // why earlier attempts showed the popovers but never highlighted
      // the actual menu items).
      {
        element: '[data-testid="header-menu-btn"]',
        popover: {
          title: '☰ Menu',
          description: 'Tap to open. Everything that isn\'t a Linktree pill lives in here — I\'ll walk you through each item next.',
          side: 'bottom',
          align: 'end',
          onNextClick: (_el, _step, { driver: d }) => {
            window.dispatchEvent(new CustomEvent('cca:tour-force-menu-open'));
            setTimeout(() => d.moveNext(), 350);
          },
        },
      },

      // Per-item menu walkthrough. Plain string selectors — the menu
      // is held open by Header's controlled-open state until we
      // release it via `cca:tour-release-menu` on the last menu step.
      {
        element: '[data-testid="menu-home"]',
        lazyTarget: true,
        onHighlightStarted: clearStaleHighlight,
        popover: { title: '🏠 Home', description: 'Jumps you back to this Linktree-style homepage at any time.', side: 'left', align: 'start' },
      },
      {
        element: '[data-testid="menu-blog"]',
        lazyTarget: true,
        onHighlightStarted: clearStaleHighlight,
        popover: { title: '📰 Blog', description: 'Blog articles for neurodivergent individuals, second language learners, and more.', side: 'left', align: 'start' },
      },
      {
        element: '[data-testid="menu-resources"]',
        lazyTarget: true,
        onHighlightStarted: clearStaleHighlight,
        popover: { title: '📚 Resources', description: 'Curated external links — ADHD/autism organizations, mental-health tools, and articles. Signed-in users can favorite resources and submit new ones for review.', side: 'left', align: 'start' },
      },
      {
        element: '[data-testid="menu-stats-leaderboard"]',
        lazyTarget: true,
        onHighlightStarted: clearStaleHighlight,
        popover: { title: '🏆 Stats & Leaderboard', description: 'Track your streaks, XP, badges, and where you rank against friends and the global community.', side: 'left', align: 'start' },
      },
      {
        element: '[data-testid="menu-for-organizations"]',
        lazyTarget: true,
        onHighlightStarted: clearStaleHighlight,
        popover: { title: '🏢 For Organizations', description: 'Bulk VIP subscriptions, workplace-ready communication training, and partnership inquiries.', side: 'left', align: 'start' },
        // Last menu step — release the controlled-open hold so the
        // dropdown can close on its own once we move past the menu.
        onDeselected: () => {
          clearStaleHighlight();
          window.dispatchEvent(new CustomEvent('cca:tour-release-menu'));
        },
      },

      // Linktree pills — alphabetical, matching the visual order.
      {
        element: '[data-testid="linktree-blog"]',
        popover: { title: '📰 Blog', description: 'Blog articles for neurodivergent individuals, second language learners, and more.', side: 'top', align: 'center' },
      },
      {
        element: '[data-testid="linktree-timer"]',
        popover: { title: '⏲️ Dino Llama Fun Timer', description: 'ADHD-friendly Pomodoro timer with a friendly dino + llama animation. Try the "Slower Llama 🐢" toggle for a gentler pace.', side: 'top', align: 'center' },
      },
      {
        element: '[data-testid="linktree-partners"]',
        popover: { title: '🏢 For Organizations', description: 'Bulk subscriptions and workplace-ready communication training.', side: 'top', align: 'center' },
      },
      {
        element: '[data-testid="linktree-friends"]',
        popover: { title: '👥 Friends', description: 'Connect with others on the same journey. Encourage each other.', side: 'top', align: 'center' },
      },
      {
        element: '[data-testid="linktree-library"]',
        popover: { title: '📚 Phrase Library', description: 'Browse 3,400+ phrases with translations and audio. The biggest tool in the app.', side: 'top', align: 'center' },
      },
      {
        element: '[data-testid="linktree-resources"]',
        popover: { title: '🔗 Resources', description: 'Curated external links — ADHD/autism orgs, helpful tools, and articles. Logged-in users can favorite and submit new ones.', side: 'top', align: 'center' },
      },
      // Thought Interpreter — corrected description: this rephrases
      // the USER's OWN thoughts in their chosen tone + regional dialect.
      // It does NOT decode someone else's words (that's the planned
      // future "Neurotypical Interpreter" companion mode).
      {
        element: '[data-testid="linktree-interpreter"]',
        popover: {
          title: '✨ Thought Interpreter',
          description: 'Type your own message and Detective Dino rewrites it in your chosen tone and regional dialect — kinder, clearer, and ready to send. Great when your first draft feels too blunt or too tangled.',
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '[data-testid="linktree-todo"]',
        popover: { title: '✅ To-Do List', description: 'Neurodivergent-friendly task list with dopamine rewards.', side: 'top', align: 'center' },
      },
      {
        element: '[data-testid="linktree-emojis"]',
        popover: {
          title: '🎨 Communication Emojis',
          description: 'Pick up to 6 AAC-style emojis, name your pack, and download a ZIP ready for Slack, Discord, Teams, Twitch, Zoom, Telegram, WhatsApp, and more — with step-by-step install instructions inside.',
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '[data-testid="linktree-google-review-cta"]',
        popover: { title: '⭐ Leave us a Google review', description: 'If the app helps you, a quick Google review helps other neurodivergent folks find us. Takes 30 seconds.', side: 'top', align: 'center' },
      },
      {
        element: '[data-testid="linktree-support"]',
        popover: { title: '🌳⭐ Support us', description: 'Donations cover server costs, audio storage, API keys, chai tea for writing blogs and phrases — and a new skill for Dinos every now and then. Any amount welcome — totally optional.', side: 'top', align: 'center' },
      },
      {
        element: '[data-testid="linktree-follow-section"]',
        popover: { title: '📲 Follow us', description: 'Find us on every major platform — Facebook, Instagram, LinkedIn, Threads, TikTok, and YouTube. New phrases and tips every week.', side: 'top', align: 'center' },
      },
      {
        popover: {
          title: '📱 Bottom navigation',
          description: 'Once you tap into Library, Timer, To-Do, or Friends, you\'ll see a bottom navigation bar with one-tap access to your main tools.',
        },
      },
      {
        popover: {
          title: 'Start your journey!',
          description: 'Create a free account to save favorites, build streaks, earn badges, add Friends, and climb the Leaderboard. Want a guided walk through the Phrase Library next?',
          doneBtnText: 'Show me the Library →',
          onCloseClick: () => {
            try { localStorage.setItem(COOKIE_KEY, '1'); } catch { /* ignore */ }
            try { drvRef.current?.destroy(); } catch { /* ignore */ }
          },
          onNextClick: () => {
            try { localStorage.setItem(COOKIE_KEY, '1'); } catch { /* ignore */ }
            try { localStorage.setItem(LIBRARY_TOUR_KEY, 'pending'); } catch { /* ignore */ }
            try { drvRef.current?.destroy(); } catch { /* ignore */ }
            drvRef.current = null;
            // Tiny delay so driver.js finishes its cleanup before the
            // route change — otherwise the leftover overlay can flash.
            setTimeout(() => navigate('/library'), 30);
          },
        },
      },
    ];

    // ----- Spanish steps (parallel to English, same step count) -----
    const stepsEs = [
      {
        element: '[data-testid="header-logo"]',
        popover: { title: '🦖 El logo es tu botón "Inicio"', description: 'Toca el logo de Detective Dino desde cualquier página para volver a esta página principal.', side: 'bottom', align: 'start' },
      },
      {
        element: '[data-testid="header-install-wrap"]',
        popover: { title: '⬇️ Instalar', description: 'Añade ClearCommunicationApp a tu pantalla de inicio como PWA, o descárgala en App Store / Play Store / Amazon Fire.', side: 'bottom', align: 'end' },
      },
      {
        element: '[data-testid="header-language-toggle"]',
        popover: { title: '🌐 Idioma', description: 'Cambia entre Inglés y Español en cualquier momento. Todo el sitio se traduce.', side: 'bottom', align: 'end' },
      },
      {
        element: '[data-testid="header-menu-btn"]',
        popover: {
          title: '☰ Menú',
          description: 'Tócalo para abrir. Aquí vive todo lo que no es un botón Linktree — te muestro cada elemento a continuación.',
          side: 'bottom',
          align: 'end',
          onNextClick: (_el, _step, { driver: d }) => {
            window.dispatchEvent(new CustomEvent('cca:tour-force-menu-open'));
            setTimeout(() => d.moveNext(), 350);
          },
        },
      },
      {
        element: '[data-testid="menu-home"]',
        lazyTarget: true,
        onHighlightStarted: clearStaleHighlight,
        popover: { title: '🏠 Inicio', description: 'Te lleva de vuelta a esta página principal estilo Linktree en cualquier momento.', side: 'left', align: 'start' },
      },
      {
        element: '[data-testid="menu-blog"]',
        lazyTarget: true,
        onHighlightStarted: clearStaleHighlight,
        popover: { title: '📰 Blog', description: 'Artículos del blog para personas neurodivergentes, estudiantes de un segundo idioma, y más.', side: 'left', align: 'start' },
      },
      {
        element: '[data-testid="menu-resources"]',
        lazyTarget: true,
        onHighlightStarted: clearStaleHighlight,
        popover: { title: '📚 Recursos', description: 'Enlaces seleccionados — organizaciones de TDAH/autismo, herramientas de salud mental y artículos. Los usuarios registrados pueden marcar favoritos y enviar nuevos.', side: 'left', align: 'start' },
      },
      {
        element: '[data-testid="menu-stats-leaderboard"]',
        lazyTarget: true,
        onHighlightStarted: clearStaleHighlight,
        popover: { title: '🏆 Estadísticas y Clasificación', description: 'Sigue tus rachas, XP, insignias, y dónde te ubicas con tus amigos y la comunidad global.', side: 'left', align: 'start' },
      },
      {
        element: '[data-testid="menu-for-organizations"]',
        lazyTarget: true,
        onHighlightStarted: clearStaleHighlight,
        popover: { title: '🏢 Para Organizaciones', description: 'Suscripciones VIP en masa, capacitación de comunicación para empresas, y consultas de colaboración.', side: 'left', align: 'start' },
        onDeselected: () => {
          clearStaleHighlight();
          window.dispatchEvent(new CustomEvent('cca:tour-release-menu'));
        },
      },
      {
        element: '[data-testid="linktree-blog"]',
        popover: { title: '📰 Blog', description: 'Artículos del blog para personas neurodivergentes, estudiantes de un segundo idioma, y más.', side: 'top', align: 'center' },
      },
      {
        element: '[data-testid="linktree-timer"]',
        popover: { title: '⏲️ Temporizador Dino Llama', description: 'Pomodoro amistoso con TDAH y un dino + llama animados. Prueba el modo "Llama Lenta 🐢" para un ritmo más suave.', side: 'top', align: 'center' },
      },
      {
        element: '[data-testid="linktree-partners"]',
        popover: { title: '🏢 Para Organizaciones', description: 'Suscripciones en masa y capacitación para empresas.', side: 'top', align: 'center' },
      },
      {
        element: '[data-testid="linktree-friends"]',
        popover: { title: '👥 Amigos', description: 'Conéctate con otras personas en el mismo viaje. Apóyense mutuamente.', side: 'top', align: 'center' },
      },
      {
        element: '[data-testid="linktree-library"]',
        popover: { title: '📚 Biblioteca de Frases', description: 'Explora más de 3,400 frases con traducciones y audio. La herramienta principal.', side: 'top', align: 'center' },
      },
      {
        element: '[data-testid="linktree-resources"]',
        popover: { title: '🔗 Recursos', description: 'Enlaces seleccionados a herramientas, organizaciones y artículos útiles.', side: 'top', align: 'center' },
      },
      {
        element: '[data-testid="linktree-interpreter"]',
        popover: {
          title: '✨ Intérprete de Pensamientos',
          description: 'Escribe tu propio mensaje y Detective Dino lo reescribe en el tono y dialecto regional que elijas — más amable, más claro, y listo para enviar. Útil cuando tu primer borrador suena brusco o enredado.',
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '[data-testid="linktree-todo"]',
        popover: { title: '✅ Lista de Tareas', description: 'Lista de tareas amigable con la neurodiversidad y recompensas de dopamina.', side: 'top', align: 'center' },
      },
      {
        element: '[data-testid="linktree-emojis"]',
        popover: {
          title: '🎨 Emojis de Comunicación',
          description: 'Elige hasta 6 emojis estilo CAA, ponle nombre a tu paquete y descarga un ZIP listo para Slack, Discord, Teams, Twitch, Zoom, Telegram, WhatsApp y más — con instrucciones de instalación paso a paso incluidas.',
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '[data-testid="linktree-google-review-cta"]',
        popover: { title: '⭐ Déjanos una reseña en Google', description: 'Si la app te ayuda, una reseña rápida en Google ayuda a otras personas neurodivergentes a encontrarnos. 30 segundos.', side: 'top', align: 'center' },
      },
      {
        element: '[data-testid="linktree-support"]',
        popover: { title: '🌳⭐ Apóyanos', description: 'Las donaciones cubren costos de servidor, almacenamiento de audio, claves API, té chai para escribir blogs y frases — ¡y una nueva habilidad para los Dinos de vez en cuando! Cualquier cantidad — totalmente opcional.', side: 'top', align: 'center' },
      },
      {
        element: '[data-testid="linktree-follow-section"]',
        popover: { title: '📲 Síguenos', description: 'Encuéntranos en Facebook, Instagram, LinkedIn, Threads, TikTok y YouTube. Frases nuevas y consejos cada semana.', side: 'top', align: 'center' },
      },
      {
        popover: {
          title: '📱 Navegación inferior',
          description: 'Cuando entras en Biblioteca, Temporizador, Tareas o Amigos, verás una barra inferior con acceso rápido a tus herramientas principales.',
        },
      },
      {
        popover: {
          title: '¡Empieza tu viaje!',
          description: 'Crea una cuenta gratis para guardar favoritos, mantener rachas, ganar insignias, añadir Amigos y subir en la Clasificación. ¿Quieres un recorrido por la Biblioteca de frases?',
          doneBtnText: 'Llévame a la Biblioteca →',
          onCloseClick: () => {
            try { localStorage.setItem(COOKIE_KEY, '1'); } catch { /* ignore */ }
            try { drvRef.current?.destroy(); } catch { /* ignore */ }
          },
          onNextClick: () => {
            try { localStorage.setItem(COOKIE_KEY, '1'); } catch { /* ignore */ }
            try { localStorage.setItem(LIBRARY_TOUR_KEY, 'pending'); } catch { /* ignore */ }
            try { drvRef.current?.destroy(); } catch { /* ignore */ }
            drvRef.current = null;
            setTimeout(() => navigate('/library'), 30);
          },
        },
      },
    ];

    const all = useEs ? stepsEs : stepsEn;
    // Drop steps whose target testid isn't currently in the DOM so a
    // renamed/missing element never breaks the whole tour. Steps
    // flagged `lazyTarget: true` (the menu-item walkthrough) bypass
    // the pre-filter because their target only mounts when the
    // dropdown is open.
    const validSteps = all.filter((s) => s.lazyTarget || !s.element || (typeof s.element === 'string' && document.querySelector(s.element)));
    if (validSteps.length === 0) return;

    const drv = driver({
      // No "X of Y" counter — felt longer than it actually was.
      showProgress: false,
      animate: true,
      smoothScroll: true,
      allowClose: true,
      nextBtnText: useEs ? 'Siguiente →' : 'Next →',
      prevBtnText: useEs ? '← Anterior' : '← Back',
      doneBtnText: useEs ? '¡Listo!' : 'Got it!',
      steps: validSteps,
      onDestroyed: () => {
        try { localStorage.setItem(COOKIE_KEY, '1'); } catch { /* ignore */ }
        // Defensive cleanup — driver.js usually handles this but mobile
        // browsers occasionally leave the body classes attached which
        // freezes pointer events.
        try {
          document.body.classList.remove('driver-active', 'driver-fade');
          document.querySelectorAll('.driver-active-element').forEach((el) => {
            el.classList.remove('driver-active-element');
          });
        } catch { /* ignore */ }
        // Belt-and-braces: release any tour-forced menu hold so the
        // user can close the dropdown normally if they exited the
        // tour mid-walkthrough.
        try { window.dispatchEvent(new CustomEvent('cca:tour-release-menu')); } catch { /* ignore */ }
        closeMenuIfOpen();
      },
    });

    drvRef.current = drv;
    drv.drive();
  }, [navigate, openMenuIfClosed, closeMenuIfOpen]);

  // ---------- event wiring ----------

  useEffect(() => {
    const handleStartEvent = () => {
      // If we're not on '/', ship the user home first — most tour
      // targets only exist on the LandingPage.
      if (pathname !== '/') {
        navigate('/');
        setTimeout(() => setShowLangPicker(true), 700);
        return;
      }
      setShowLangPicker(true);
    };

    window.addEventListener('cca:start-tour', handleStartEvent);
    return () => {
      window.removeEventListener('cca:start-tour', handleStartEvent);
      if (drvRef.current) {
        try { drvRef.current.destroy(); } catch { /* ignore */ }
        drvRef.current = null;
      }
    };
  }, [pathname, navigate]);

  // Language picker handler — closes the dialog, kicks off the tour
  // after a short delay so Dialog's exit animation finishes (otherwise
  // driver.js's first highlight scrolls behind the still-fading modal).
  const handlePickLanguage = (lang) => {
    setShowLangPicker(false);
    setTimeout(() => runTour(lang), 250);
  };

  // Default the picker's emphasis to whichever language the site is
  // currently in — but always show BOTH choices.
  const emphasizeEs = siteIsSpanish;

  return (
    <Dialog open={showLangPicker} onOpenChange={setShowLangPicker}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="tour-language-picker"
      >
        <DialogHeader>
          <DialogTitle className="text-center text-lg">
            🦖 Tour
          </DialogTitle>
          <DialogDescription className="text-center text-base leading-relaxed pt-2">
            Would you like to view this tour in English<br />
            <span className="italic">¿o le gustaría ver este recorrido en español?</span>
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-3 justify-center pt-2">
          <Button
            type="button"
            variant={emphasizeEs ? 'outline' : 'default'}
            className={emphasizeEs ? '' : 'bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00D9C0]'}
            onClick={() => handlePickLanguage('en')}
            data-testid="tour-lang-en"
          >
            English
          </Button>
          <Button
            type="button"
            variant={emphasizeEs ? 'default' : 'outline'}
            className={emphasizeEs ? 'bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00D9C0]' : ''}
            onClick={() => handlePickLanguage('es')}
            data-testid="tour-lang-es"
          >
            Español
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FirstVisitTour;
